import React, { useMemo, useState } from 'react';
import type { components } from '../../types/learnerApi';
import { toDisplayBuckets, type DisplayBucket } from '../../utils/courseBuckets';
import CourseCard from './CourseCard';

type CourseBuckets = components['schemas']['CourseBuckets'];
type CourseCardData = components['schemas']['CourseCard'];

const TABS: { key: DisplayBucket; label: string }[] = [
  { key: 'not_started', label: 'Not Started' },
  { key: 'in_progress', label: 'In Progress' },
  // Backend `completed` cards + cards whose end_date has passed — see
  // toDisplayBuckets for the reclassification rules.
  { key: 'finalized', label: 'Ended' },
];

interface CourseBucketTabsProps {
  buckets: CourseBuckets;
}

const CourseBucketTabs: React.FC<CourseBucketTabsProps> = ({ buckets }) => {
  const display = useMemo(() => toDisplayBuckets(buckets), [buckets]);

  const [activeTab, setActiveTab] = useState<DisplayBucket>(() => {
    if (display.in_progress.length > 0) return 'in_progress';
    if (display.not_started.length > 0) return 'not_started';
    return 'finalized';
  });

  const courses: CourseCardData[] = display[activeTab];

  const countFor = (key: DisplayBucket) => display[key].length;

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-border">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === key
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {label}
            {countFor(key) > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === key
                  ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                  : 'bg-neutral-100 dark:bg-neutral-800 text-text-secondary'
              }`}>
                {countFor(key)}
              </span>
            )}
            {activeTab === key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {courses.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-text-secondary text-sm">
          No {TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} courses.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {courses.map((course) => (
            <CourseCard key={course.assignment_id ?? course.course_id} course={course} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CourseBucketTabs;
